import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import { formatDate } from "../../../helpers/appHelpers";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface ActionType {
	name: string;
}

interface GrievanceType {
	name: string;
}

interface GrievanceReport {
	reportNumber: string;
}

interface DisciplinaryActionRow {
	id: number;
	actionNumber: string;
	employee?: Employee;
	actionType: ActionType;
	notes: string;
	actionDate: string;
	grievanceReport?: GrievanceReport;
	createdAt?: string;
	modifiedAt?: string;
}

interface GrievanceReportRow {
	id: number;
	reportNumber: string;
	reportingEmployee?: Employee;
	grievanceType: GrievanceType;
	note: string;
	reportDate: string;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Disciplinary Actions Columns ==========
export const disciplinaryActionsColumns: ColumnDef<DisciplinaryActionRow>[] = [
	{
		id: "actionNumber",
		accessorKey: "actionNumber",
		header: "Action Number",
		size: 180,
	},
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="employee"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{employee.firstName + " " + employee.lastName}
					</p>
				</div>
			);
		},
	},
	{
		id: "actionType",
		accessorKey: "actionType",
		header: "Action Type",
		size: 200,
		cell: ({ row }) => row.original.actionType.name,
	},
	{
		id: "notes",
		accessorKey: "notes",
		header: "Note",
		size: 350,
	},
	{
		id: "actionDate",
		accessorKey: "actionDate",
		header: "Action Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.actionDate),
	},
	{
		id: "grievanceReport",
		accessorKey: "grievanceReport",
		header: "Grievance Report Number",
		size: 280,
		cell: ({ row }) => row.original.grievanceReport?.reportNumber || "--",
	},
];

export const disciplinaryActionsColumnVisibility: Record<string, boolean> = {
	actionNumber: true,
	employee: true,
	actionType: true,
	notes: true,
	actionDate: true,
	grievanceReport: false,
};

// ========== Grievance Reports Columns ==========
export const grievanceReportsColumns: ColumnDef<GrievanceReportRow>[] = [
	{
		id: "reportNumber",
		accessorKey: "reportNumber",
		header: "Report Number",
		size: 180,
	},
	{
		id: "reportingEmployee",
		accessorKey: "reportingEmployee",
		header: "Reporting Employee",
		size: 250,
		cell: ({ row }) => {
			const reportingEmployee = row.original.reportingEmployee;
			if (!reportingEmployee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={reportingEmployee.photoUrl || ""}
						alt="reportingEmployee"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{reportingEmployee.firstName + " " + reportingEmployee.lastName}
					</p>
				</div>
			);
		},
	},
	{
		id: "grievanceType",
		accessorKey: "grievanceType",
		header: "Grievance Type",
		size: 200,
		cell: ({ row }) => row.original.grievanceType.name,
	},
	{
		id: "note",
		accessorKey: "note",
		header: "Note",
		size: 380,
	},
	{
		id: "reportDate",
		accessorKey: "reportDate",
		header: "Report Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.reportDate),
	},
];

export const grievanceReportsColumnVisibility: Record<string, boolean> = {
	reportNumber: true,
	reportingEmployee: true,
	grievanceType: true,
	note: true,
	reportDate: true,
};
